import { AsyncPipe, CommonModule, TitleCasePipe } from '@angular/common';
import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { MenuItem } from 'primeng/api';

import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';

import { JobDetailsV2Fragment, Jobs_ClosedReasons } from '../../../../generated/graphql.generated';
import { CLOSE_JOB_ONGOING_EVENT_STATUSES, JOB_STAGES } from '../../../global.constants';
import { strToTitleCase } from '../../../js';
import { FreyaHelperService } from '../../../services/freya-helper.service';
import { createConfigSelector } from '../../../state/branding.store';
import { selectJobClosedReason } from '../../job-state/job-tool.reducer';
import { selectJob, selectJobInCurrentZone } from '../../job-tool.reducer';

import { JobStatusDropdownActions } from './job-status-dropdown.actions';

@Component({
  selector: 'app-job-status-dropdown',
  standalone: true,
  imports: [
    MenuModule,
    TieredMenuModule,
    TitleCasePipe,
    AsyncPipe,
    CommonModule,
  ],
  templateUrl: './job-status-dropdown.component.html',
  styleUrl: './job-status-dropdown.component.scss',
})
export class JobStatusDropdownComponent {

  public viewModel$ = this.store.select(createSelector({
    job: selectJob,
    closedReasons: createConfigSelector('JOBS_CLOSEDREASONS'),
    jobClosedReason: selectJobClosedReason,
    jobStatusItems: createSelector(
      selectJob,
      createConfigSelector('JOBS_CLOSEDREASONS'),
      selectJobInCurrentZone,
      (job, closedReasons, jobInCurrentZone) => this._createJobStatusMenuItems(job, closedReasons, jobInCurrentZone),
    ),
  }));

  public actions = JobStatusDropdownActions;

  constructor(
    public store: Store,
    private freyaHelper: FreyaHelperService,
  ) {}

  private _createJobStatusMenuItems(
    job: JobDetailsV2Fragment,
    closedReasons: Jobs_ClosedReasons[],
    jobInCurrentZone: boolean,
  ): MenuItem[] {
    if (!job) return [];
    return [
      job.stage === 'lead' && {
        label: 'Review Lead',
        icon: 'pi pi-eye',
        routerLink: ['/jobs', job.id, 'edit'],
      },
      this._createNextStepAction(job, jobInCurrentZone),
      this._createOpenAction(job, jobInCurrentZone),
      this._createCloseAction(job, closedReasons, jobInCurrentZone),
    ].filter(Boolean);
  }

  _createNextStepAction(job: JobDetailsV2Fragment, jobInCurrentZone: boolean) {

    if (job.closedAt) { return; }

    const currentStage = JOB_STAGES[job.stage];

    if (!currentStage) { return; }

    const nextStage = Object.values(JOB_STAGES).find((s) => s.order === currentStage.order + 1);

    if (!nextStage) { return; }

    return {
      label: `Move to ${strToTitleCase(nextStage.name)}`,
      icon: 'pi pi-receipt',
      command: () => this.store.dispatch(this.actions.moveToNextStageMenuItemClicked({ stage: nextStage.name })),
      disabled: !jobInCurrentZone,
    };
  }

  private _createOpenAction(
    job: JobDetailsV2Fragment,
    jobInCurrentZone: boolean,
  ) {

    const { showOpen } = this.freyaHelper.getJobCloseActionState(job);

    if (!showOpen) {
      return;
    }

    return {
      label: 'Open',
      icon: 'pi pi-circle',
      command: () => this.store.dispatch(this.actions.openJobMenuItemClicked()),
      disabled: !jobInCurrentZone,
    }
  }

  private _createCloseAction(
    job: JobDetailsV2Fragment,
    closedReasons: Jobs_ClosedReasons[],
    jobInCurrentZone: boolean,
  ): MenuItem {

    const { disableClose, reason, showClose } = this.freyaHelper.getJobCloseActionState(job, jobInCurrentZone, true);

    if (!showClose) {
      return;
    }

    const hasPendingEvents = job.events.some((e) => CLOSE_JOB_ONGOING_EVENT_STATUSES.includes(e.status));

    if (hasPendingEvents) {
      return {
        label: 'Close Job',
        icon: 'pi pi-circle-fill',
        disabled: disableClose,
        tooltip: reason,
        command: () => this.store.dispatch(this.actions.closeJobMenuItemClicked()),
      };
    }

    return {
      label: 'Close Job',
      icon: 'pi pi-circle-fill',
      disabled: disableClose,
      tooltip: reason,
      items: closedReasons
        .filter((cr) => {
          if (!cr.stages?.length) { return true; }
          return cr.stages.includes(job.stage);
        })
        .map((r) => ({
          label: r.title,
          id: r.id,
          command: () => this.store.dispatch(this.actions.closureReasonMenuItemSelected({ closedReason: r.id })),
        })),
    };
  }

}
